import { CommonModule } from '@angular/common';
import { Component, signal, computed, WritableSignal, ViewChild } from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { ProgressBarModule } from 'primeng/progressbar';
import { FileUploadModule, FileSelectEvent, FileUpload } from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import * as XLSX from 'xlsx';
import { SchoolService } from '../../../services/school/school.service';
import { AssessmentType } from '../../../models/enums/assessmentType';
import { ImportSchoolStudentResponse, ImportSchoolStudentResponseItem } from '../../../models/schools/importSchoolStudentResponse';
import { MessageService } from 'primeng/api';

interface UploadResult {
  row: any;
  success: boolean;
  error?: string;
}

@Component({
  selector: 'app-upload-student-data',
  standalone: true,
  imports: [CommonModule, DialogModule, ProgressBarModule, FileUploadModule, ButtonModule],
  templateUrl: './upload-student-data.component.html',
  styleUrl: './upload-student-data.component.scss',
})
export class UploadStudentDataComponent {
  dialogVisible = signal(false);
  fileName = signal('');
  totalRecords = signal(0);
  processedRecords = signal(0);
  failedRecords: WritableSignal<UploadResult[]> = signal([]);
  progress = signal(0);
  isUploading = signal(false);
  uploadSummary = signal<{ success: number; failed: number }>({ success: 0, failed: 0 });

  @ViewChild(FileUpload) fileUpload?: FileUpload;

  constructor(private http: HttpClient,
    private schoolService: SchoolService,
    private messageService: MessageService
  ) { }

  onFileSelect(event: FileSelectEvent): void {
    const file = event.files?.[0];
    if (file) {
      this.fileName.set(file.name);
      this.parseExcel(file);
    }
  }

  parseExcel(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try{
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        if (!workbook.SheetNames.length) {
          throw new Error('No sheets found in Excel file.');
        }
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
        if (!jsonData.length || !jsonData[0]['Sr.No']) {
          throw new Error('Invalid file format. Required columns are missing.');
        }
        this.totalRecords.set(jsonData.length);
        this.dialogVisible.set(true);
        this.processedRecords.set(0);
        this.failedRecords.set([]);
        this.progress.set(0);
        this.uploadSummary.set({ success: 0, failed: 0 });
        this.uploadChunks(jsonData);
      }catch(err: any){
        this.messageService.add({severity: 'error', summary: 'Error', detail: 'Error reading file'})
        this.closeDialog();
      }
    };
    reader.onerror = () => {
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Failed to read the file. The file may be corrupted.'})
      this.closeDialog();
    };
    reader.readAsArrayBuffer(file);
  }

  async uploadChunks(data: any[]): Promise<void> {
    this.isUploading.set(true);
    const chunkSize = 5;
    let processed = 0;
    let failed: UploadResult[] = [];
    let successCount = 0;
    for (let i = 0; i < data.length; i += chunkSize) {
      const chunk = data.slice(i, i + chunkSize);
      const mappedChunk = chunk.map(row => ({
        serialNumber: Number(row['Sr.No']),
        firstName: row['First_name']?.toString() || null,
        lastName: row['Last_name']?.toString() || null,
        email: row['Email']?.toString() || null,
        class: row['Class'] !== undefined && row['Class'] !== null ? row['Class'].toString() : null,
        section: row['Section']?.toString() || null,
        highSchoolName: row['School'] && row['School'] !== 'N/A' ? row['School'].toString() : null,
        password: row['Password']?.toString() || null,
        assessmentType: this.mapAssessmentType(row['Assessment']?.toString())
      }));
      try {
        //TODO: Create a service to that will call the APIs to update bulk student data
        const response: ImportSchoolStudentResponse = await firstValueFrom(this.schoolService.importSchoolStudents(mappedChunk));

        // Assume response = { success: [{rowIndex: number}], failed: [{rowIndex: number, error: string}] }
        if (response && Array.isArray(response.failedRecordsList)) {
          response.failedRecordsList.forEach((fail: any) => {
            failed.push({ row: fail, success: false, error: fail.errorMessage || undefined });
          });
        }
        if (response && Array.isArray(response.successRecords)) {
          successCount += response.successRecords;
        }
      } catch (error: any) {
        // If the whole chunk fails, mark all as failed
        chunk.forEach(row => {
          failed.push({ row, success: false, error: (error as HttpErrorResponse)?.message || 'Upload failed' });
        });
      }
      processed += chunk.length;
      this.processedRecords.set(processed);
      this.failedRecords.set([...failed]);
      this.progress.set(Math.round((processed / data.length) * 100));
    }
    this.isUploading.set(false);
    this.uploadSummary.set({ success: data.length - failed.length, failed: failed.length });
  }

  downloadFailedResults(): void {
    const failed = this.failedRecords();
    if (!failed.length) return;
    // Include all original row data except __EMPTY columns, and add Error as the last column
    // const data = failed.map(f => {
    //   // Remove any keys that start with '__EMPTY'
    //   const cleanedRow: Record<string, any> = {};
    //   Object.keys(f.row).forEach(key => {

    //     if (!key.startsWith('__EMPTY')) {
    //       cleanedRow[key] = f.row[key];
    //     }
    //   });
    //   cleanedRow['Error'] = f.error;
    //   return cleanedRow;
    // });

    //No need to filter out _Empty here
    //Ab direct response ko as excel download kr rha hai
    const data = failed.map(f => {
      const errorRow = f.row as ImportSchoolStudentResponseItem;
      return {
        'Sr.No': errorRow.serialNumber,
        'First Name': errorRow.firstName || '',
        'Last Name': errorRow.lastName || '',
        'Email': errorRow.email || '',
        'Class': errorRow.class || '',
        'Section': errorRow.section || '',
        'School': errorRow.school || '',
        'Password': errorRow.password || '',
        'Assessment Type': AssessmentType[errorRow.assessmentType],
        'Error': f.error || errorRow.errorMessage || 'Unknown error'
      };
    });
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Failed Records');
    XLSX.writeFile(workbook, 'failed-student-uploads.xlsx');
  }

  mapAssessmentType(value: string): AssessmentType {
    switch ((value || '').trim().toLowerCase()) {
      case 'career':
        return AssessmentType.Career;
      case 'stream':
        return AssessmentType.Stream;
      default:
        return AssessmentType.None;
    }
  }

  closeDialog(): void {

    this.dialogVisible.set(false);
    this.fileName.set('');
    this.totalRecords.set(0);
    this.processedRecords.set(0);
    this.failedRecords.set([]);
    this.progress.set(0);
    this.uploadSummary.set({ success: 0, failed: 0 });
    this.fileUpload?.clear();
  }
}